// Story 4.2 — /vault SPA page. The EPIC-4 KEYSTONE READER.
//
// First production composition of <SealedDossier size="thumb"> +
// <Certificate size="thumb"> + <Skeleton> + <RevealCeremony> as
// inline overlay. Driven by useVaultItems (Story 4.2 NEW hook) +
// useReveal (Story 3.6, extended in 3.8 + 3.9 + 4.2).
//
// Layout:
//   * Filter+sort bar: state, tier, drop, sort.
//   * Responsive grid: 1/2/3/4/5 cols per breakpoint per UX-spec L748-L753.
//   * Pagination footer with Prev/Next + page-size selector (24/36/48).
//   * Empty state per UX-DR28 + UX-spec L1892.
//   * Loading skeleton per UX-spec L1899 (NEVER a spinner for grid).
//   * Error state with Retry button.
//
// Clicking ANY item opens the <CollectionItemDialog> detail popup (grows
// out from the clicked card) with the per-item actions. From there:
//   * sealed dossier → "Open" triggers useReveal() in-place; the
//     <RevealCeremony> mounts as a full-viewport overlay; onComplete
//     invalidates ["vault-items"] (Story 4.2 useReveal extension) → the
//     grid auto-refreshes with the item now in opened state.
//   * opened certificate → "View full details" deep-links to
//     /vault/certificates/:id; "List on the market" / "Search similar"
//     dispatch the marketplace flows.
//
// No auth guard — backend 401s if not logged in; the page surfaces a
// "Sign in" toast + Link to "/" on 401 errors. Follows the same
// pattern as Story 3.9 WelcomePage + Story 1.17 WalletPage.

import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Button,
  PatronageCertificate,
  RevealCeremony,
  SealedDossier,
  Skeleton,
  formatIssueNumber,
  type CertificateShare,
  type SealedDossierData,
} from "@greenhold/components";

import { EmptyState as SharedEmptyState } from "../components/EmptyState";
import { certSerialFromShareId } from "../lib/certSerial";
import { CollectionItemDialog } from "../features/vault/CollectionItemDialog";
import { certPropsFromShare } from "../features/vault/certProps";
import { ListForSaleDialog } from "../features/marketplace/ListForSaleDialog";
import { ListSealedDossierForSaleDialog } from "../features/marketplace/ListSealedDossierForSaleDialog";
import {
  EditSealedDossierListingDialog,
  EditShareListingDialog,
} from "../features/marketplace/EditListingDialog";
import type { ShareTier } from "../features/marketplace/useShareTierFloorPrice";
import { useReveal } from "../features/reveal/useReveal";
import { useDropForPurchase } from "../features/dossier-purchase/useDropForPurchase";
import { useEffectiveMotionMode } from "../features/preferences/useEffectiveMotionMode";
import { useVaultItems } from "../features/vault/useVaultItems";
import { deriveShouldShowRetirementWarning } from "../features/vault/vaultItemHelpers";
import { TradeInPanel } from "../features/vault/TradeInPanel";
import { ShowcasePane } from "../features/vault/ShowcasePane";
import {
  ShowcaseContextMenu,
  type ShowcaseMenuTarget,
} from "../features/vault/ShowcaseContextMenu";
import {
  showcaseItemId,
  useSetShowcaseMembership,
  useShowcase,
} from "../features/vault/useShowcase";
import { SHOWCASE_DND_MIME, writeShowcaseDrag } from "../features/vault/showcaseDnd";
import type {
  SealedDossierVaultRow,
  VaultFilter,
  VaultItem,
  VaultSort,
  VaultTier,
} from "../features/vault/vaultTypes";

// Multiples of 20 so a page fills whole rows at the desktop grid widths
// (4 cols at lg, 5 at xl) — 24 left an orphan row of 4 at 5-up.
const PAGE_SIZE_OPTIONS = [20, 40, 60] as const;
const DEFAULT_PAGE_SIZE = 20;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

const FILTER_OPTIONS: { value: VaultFilter; label: string }[] = [
  { value: "all", label: "All items" },
  { value: "sealed", label: "Sealed only" },
  { value: "opened", label: "Opened only" },
];

const TIER_OPTIONS: { value: VaultTier; label: string }[] = [
  { value: "all", label: "Every tier" },
  { value: "common", label: "Common" },
  { value: "preferred", label: "Preferred" },
  { value: "premium", label: "Premium" },
  { value: "founders", label: "Founder's" },
];

const SORT_OPTIONS: { value: VaultSort; label: string }[] = [
  { value: "acquired_at_desc", label: "Acquired (newest first)" },
  { value: "acquired_at_asc", label: "Acquired (oldest first)" },
  { value: "tier", label: "Tier" },
  { value: "market_price_desc", label: "Marketplace price" },
];

/// Convert a SealedDossierVaultRow into the SealedDossierData shape the
/// component prop expects. Forward-compatible field mapping; the vault
/// row carries an extra acquired_at the component doesn't read.
function dossierFromVaultRow(row: SealedDossierVaultRow): SealedDossierData {
  return {
    id: row.id,
    drop_id: row.drop_id,
    edition_number: row.edition_number,
    portfolio_title: row.portfolio_title,
    drop_status: row.drop_status,
    retiring_at: row.retiring_at,
    seal_color: row.seal_color,
  };
}

export function VaultPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") ?? "1") || 1;
  const perPage = (Number(searchParams.get("per_page") ?? String(DEFAULT_PAGE_SIZE)) ||
    DEFAULT_PAGE_SIZE) as PageSize;
  const filter = (searchParams.get("filter") ?? "all") as VaultFilter;
  const tier = (searchParams.get("tier") ?? "all") as VaultTier;
  const sort = (searchParams.get("sort") ?? "acquired_at_desc") as VaultSort;
  const dropId = searchParams.get("drop_id");

  const query = useVaultItems({
    page,
    per_page: perPage,
    filter,
    tier,
    drop_id: dropId,
    sort,
  });

  const navigate = useNavigate();
  // The sealed dossier the reveal ceremony is currently opening. Set when the
  // detail popup's "Open dossier" is clicked; the ceremony mounts in its
  // `approach` state and fires the actual reveal on "Break the seal".
  const [openingSealed, setOpeningSealed] = useState<SealedDossierVaultRow | null>(
    null,
  );
  // Story 5.3 — "List for sale" trigger for sealed dossiers in the
  // vault grid. The dialog mounts at the page level; clicking the per-
  // card Button sets this target. Sibling DOM affordance to the
  // click-to-reveal button so the two click targets don't conflict.
  const [listForSaleTarget, setListForSaleTarget] = useState<SealedDossierVaultRow | null>(
    null,
  );
  // "List for sale" trigger for opened certificates (mirrors the sealed
  // target above; opened shares use the ListForSaleDialog).
  const [listCertificateTarget, setListCertificateTarget] = useState<Extract<
    VaultItem,
    { kind: "opened" }
  > | null>(null);
  // "Edit listing" trigger for an already-listed collection item. The detail
  // popup sets this (the item carries its active_listing); the edit dialog
  // mounts at page level.
  const [editListingItem, setEditListingItem] = useState<VaultItem | null>(null);

  // Collection item detail popup — clicking any card opens this (instead of
  // instantly revealing a sealed dossier / redirecting for an opened cert).
  // `detailOrigin` is the click point so the popup grows out from the card.
  const [detailTarget, setDetailTarget] = useState<VaultItem | null>(null);
  const [detailOrigin, setDetailOrigin] = useState<{ x: number; y: number } | null>(
    null,
  );

  function openDetail(item: VaultItem, event: MouseEvent) {
    // Keyboard activation (Enter/Space) reports clientX/Y = 0 — fall back
    // to a centered pop instead of growing from the top-left corner.
    const fromKeyboard = event.detail === 0;
    setDetailOrigin(
      fromKeyboard ? null : { x: event.clientX, y: event.clientY },
    );
    setDetailTarget(item);
  }

  // Edge auto-scroll during a showcase drag: the native HTML5 drag doesn't
  // scroll the page, so dragging a grid card up toward the Showcase pane (once
  // it has scrolled out of view) left nowhere to drop it. While a showcase
  // drag is in flight, nudge the window when the cursor nears the top/bottom
  // viewport edge.
  useEffect(() => {
    const EDGE_PX = 90; // trigger band at each edge
    const STEP_PX = 20; // scroll delta per dragover tick
    function onDragOver(event: globalThis.DragEvent) {
      const types = event.dataTransfer?.types;
      if (!types || !Array.from(types).includes(SHOWCASE_DND_MIME)) return;
      const y = event.clientY;
      if (y < EDGE_PX) {
        window.scrollBy(0, -STEP_PX);
      } else if (y > window.innerHeight - EDGE_PX) {
        window.scrollBy(0, STEP_PX);
      }
    }
    window.addEventListener("dragover", onDragOver);
    return () => window.removeEventListener("dragover", onDragOver);
  }, []);

  // Epic 10 Showcase — the set of item ids currently in the owner's showcase,
  // so a grid item can badge itself "in showcase" and the right-click menu can
  // offer add vs remove. `setMembership` drives that menu action.
  const showcase = useShowcase();
  const showcaseIds = useMemo(
    () =>
      new Set((showcase.data?.data.items ?? []).map((it) => showcaseItemId(it))),
    [showcase.data],
  );
  const setMembership = useSetShowcaseMembership();
  const [menu, setMenu] = useState<ShowcaseMenuTarget | null>(null);

  function openShowcaseMenu(
    event: MouseEvent,
    kind: "certificate" | "sealed_dossier",
    id: string,
    label: string,
  ) {
    event.preventDefault();
    setMenu({
      x: event.clientX,
      y: event.clientY,
      kind,
      id,
      label,
      inShowcase: showcaseIds.has(id),
    });
  }

  function toggleShowcaseMembership(target: ShowcaseMenuTarget) {
    setMembership.mutate(
      { kind: target.kind, id: target.id, state: !target.inShowcase },
      {
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : "Showcase update failed",
          ),
        onSuccess: () =>
          toast.success(
            target.inShowcase ? "Removed from showcase" : "Added to showcase",
          ),
      },
    );
  }

  // Share Trade-in — selection mode over opened shares. `selectedIds`
  // holds the chosen share_ids; the panel sums their tier points and
  // fires the burn+mint once the total reaches 10.
  const [tradeInMode, setTradeInMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  function exitTradeIn() {
    setTradeInMode(false);
    setSelectedIds(new Set());
  }

  function toggleSelected(shareId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(shareId)) {
        next.delete(shareId);
      } else {
        next.add(shareId);
      }
      return next;
    });
  }

  function updateParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "" || value === "all") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    // Reset to page 1 when filters/sort change (typical pagination UX).
    if (key !== "page") {
      next.delete("page");
    }
    setSearchParams(next);
  }

  function handleSortChange(value: string) {
    updateParam("sort", value === "acquired_at_desc" ? null : value);
  }

  // "Open dossier" only mounts the ceremony (in its `approach` state). The
  // actual server reveal fires from the ceremony's "Break the seal" — see
  // RevealCeremonyMount below.
  function handleOpenSealed(row: SealedDossierVaultRow) {
    setOpeningSealed(row);
  }

  function handleCeremonyComplete() {
    setOpeningSealed(null);
  }

  // 401 → ask the user to sign in. Other errors → render the error
  // card. The 401 toast fires once per error transition.
  if (query.isError) {
    if (query.error.status === 401) {
      return (
        <div className="min-h-[calc(100vh-3.5rem)] bg-brand-paper-200 text-brand-forest-900">
          <main className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 pb-16 pt-8">
            <h1 className="font-heading text-3xl font-bold">Your Collection</h1>
            <section className="space-y-3 rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-6 text-brand-paper-100 shadow-md">
              <p className="font-body text-sm opacity-80">
                Sign in to view your collected dossiers and certificates.
              </p>
              <Link to="/" className="text-sm text-brand-gold-300 underline">
                Back to home →
              </Link>
            </section>
          </main>
        </div>
      );
    }
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-brand-paper-200 text-brand-forest-900">
        <main className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 pb-16 pt-8">
          <h1 className="font-heading text-3xl font-bold">Your Collection</h1>
          <section className="space-y-3 rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-6 text-brand-paper-100 shadow-md">
            <p className="font-body text-sm opacity-80">
              We couldn't load your collection ({query.error.message}).
            </p>
            <Button variant="primary" onClick={() => query.refetch()}>
              Try again
            </Button>
          </section>
        </main>
      </div>
    );
  }

  const items = query.data?.data ?? [];
  const pageInfo = query.data?.page_info;

  // Share Trade-in — the opened shares on this page eligible for
  // selection (an active marketplace listing excludes a share; the user
  // must delist first). `selectedShares` is the subset the panel scores.
  const openedShares = items
    .filter((it): it is Extract<VaultItem, { kind: "opened" }> => it.kind === "opened")
    .map((it) => ({ share: it.share, hasActiveListing: it.has_active_listing === true }));
  const selectedShares = openedShares
    .filter((o) => selectedIds.has(o.share.share_id))
    .map((o) => o.share);
  const hasOpenedShares = openedShares.length > 0;

  return (
    // Parchment page ground (like Feed / Shop / Market); the title sits on the
    // paper, and the collection itself lives inside a framed forest inset —
    // "the dark forest ground is reserved for artifact display".
    <div className="min-h-[calc(100vh-3.5rem)] bg-brand-paper-200 text-brand-forest-900">
    <main className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 pb-16 pt-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="font-heading text-3xl font-bold">Your Collection</h1>
          <p className="font-body text-sm opacity-70">
            Every sealed dossier and opened certificate you own, in one place.
          </p>
        </div>
        {!tradeInMode && hasOpenedShares && (
          <Button
            variant="secondary"
            onClick={() => setTradeInMode(true)}
            data-testid="enter-trade-in"
          >
            Trade in shares
          </Button>
        )}
      </header>

      {/* Forest inset holding the filters + artifact grid — fits the content
          width (matches the header above). Its controls are already dark-
          styled, so they read correctly on the forest ground. */}
      <section className="flex flex-col gap-[var(--gh-rhythm-consumer,24px)] rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-5 text-brand-paper-100 shadow-md">
      {tradeInMode && (
        <TradeInPanel selectedShares={selectedShares} onExit={exitTradeIn} />
      )}

      {/* Epic 10 — the Showcase pane: the owner's arranged public collection.
          Hidden during trade-in selection so the two modes don't compete. */}
      {!tradeInMode && <ShowcasePane />}

      <FilterBar
        filter={filter}
        tier={tier}
        sort={sort}
        onFilterChange={(v) => updateParam("filter", v === "all" ? null : v)}
        onTierChange={(v) => updateParam("tier", v === "all" ? null : v)}
        onSortChange={handleSortChange}
      />

      {query.isLoading ? (
        <VaultGridSkeleton />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <ul
          role="list"
          aria-busy={query.isFetching}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
        >
          {items.map((item) => {
            if (item.kind === "sealed") {
              const row = item.sealed_dossier;
              const showWarning = deriveShouldShowRetirementWarning(row);
              const state =
                row.drop_status === "retired" ? "retired" : "idle";
              const sealedListed = item.has_active_listing === true;
              return (
                <li
                  key={row.id}
                  role="listitem"
                  className={`flex flex-col items-center gap-2 ${
                    tradeInMode ? "pointer-events-none opacity-40" : ""
                  }`}
                >
                  {/* Relative wrapper so the "Listed" marketplace badge can
                      overlay the artifact without shifting the grid. */}
                  <div className="relative inline-block">
                  <button
                    type="button"
                    disabled={tradeInMode}
                    // w-32 (128px) matches the title caption width (max-w-[8rem])
                    // so the artifact fills the "Streamer Dossier 1" column. The
                    // definite width is required — without it the button is
                    // shrink-to-fit and SealedDossier's width:100% (all content
                    // absolutely positioned, min-content 0) collapses to ~1px.
                    // size="md" (cap 240) lets it fill 128px; "thumb" caps at 96.
                    className="block w-32 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2"
                    onClick={(e) => openDetail(item, e)}
                    draggable={!tradeInMode}
                    onDragStart={(e) =>
                      writeShowcaseDrag(e, {
                        source: "grid",
                        kind: "sealed_dossier",
                        id: row.id,
                      })
                    }
                    onContextMenu={(e) =>
                      openShowcaseMenu(e, "sealed_dossier", row.id, row.portfolio_title)
                    }
                    aria-label={`Sealed dossier from ${row.portfolio_title}, issue ${row.edition_number}${sealedListed ? ", listed on the marketplace" : ""}. Open details.`}
                  >
                    <SealedDossier
                      dossier={dossierFromVaultRow(row)}
                      size="md"
                      interactive
                      showRetirementWarning={showWarning}
                      state={state}
                    />
                  </button>
                  {sealedListed && <ListedBadge />}
                  {showcaseIds.has(row.id) && <ShowcaseBadge />}
                  </div>
                  {/* Title caption — so items are distinguishable at a
                      glance (the artifact face alone reads as generic). */}
                  <div className="w-full max-w-[8rem] text-center">
                    <p
                      className="truncate font-body text-sm font-semibold"
                      title={row.portfolio_title}
                    >
                      {row.portfolio_title}
                    </p>
                    <p
                      className="truncate font-body text-[11px] opacity-60"
                      title={row.portfolio_theme ?? undefined}
                    >
                      {row.portfolio_theme ??
                        `Issue #${formatIssueNumber(row.edition_number)}`}
                    </p>
                  </div>
                </li>
              );
            }
            const share = item.share;
            const hasActiveListing = item.has_active_listing === true;
            const isSelected = selectedIds.has(share.share_id);
            // Trade-in mode — opened shares become selectable. A share on
            // an active listing is excluded (delist first).
            const certThumb = (
              <PatronageCertificate width={128} {...certPropsFromShare(share)} />
            );
            return (
              <li
                key={share.share_id}
                role="listitem"
                className="flex flex-col items-center gap-1"
              >
                {/* Story 4.4 — wrap in a relative container so the
                    indicator badges can absolute-position over the
                    thumbnail without affecting the grid layout. */}
                <div className="relative inline-block">
                  {tradeInMode ? (
                    <button
                      type="button"
                      disabled={hasActiveListing}
                      onClick={() => toggleSelected(share.share_id)}
                      aria-pressed={isSelected}
                      aria-label={`Select certificate: ${share.snapshot_display_name}, ${share.tier}, issue ${share.edition_number}`}
                      data-testid={`trade-in-card-${share.share_id}`}
                      className={`block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2 ${
                        isSelected
                          ? "ring-2 ring-tier-founders-fill ring-offset-2 ring-offset-brand-forest-900"
                          : ""
                      } ${hasActiveListing ? "cursor-not-allowed opacity-40" : ""}`}
                    >
                      {certThumb}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => openDetail(item, e)}
                      draggable
                      onDragStart={(e) =>
                        writeShowcaseDrag(e, {
                          source: "grid",
                          kind: "certificate",
                          id: share.share_id,
                        })
                      }
                      onContextMenu={(e) =>
                        openShowcaseMenu(
                          e,
                          "certificate",
                          share.share_id,
                          share.snapshot_display_name,
                        )
                      }
                      className="block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2"
                      aria-label={`Certificate: ${share.snapshot_display_name}, ${share.tier}, issue ${share.edition_number}${hasActiveListing ? ", listed on the marketplace" : ""}. Open details.`}
                    >
                      {certThumb}
                    </button>
                  )}
                  {!tradeInMode && hasActiveListing && <ListedBadge />}
                  {!tradeInMode && showcaseIds.has(share.share_id) && (
                    <ShowcaseBadge />
                  )}
                  {(share.is_highlighted_by_owner ||
                    share.is_favourited_by_owner) && (
                    <div className="pointer-events-none absolute bottom-1 right-1 flex gap-1">
                      {share.is_highlighted_by_owner && (
                        <span
                          aria-label="Highlighted on your public profile"
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-tier-founders-fill text-[10px] text-brand-paper-100 shadow"
                        >
                          ★
                        </span>
                      )}
                      {share.is_favourited_by_owner && (
                        <span
                          aria-label="Favourited (private)"
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-state-error-fill text-[10px] text-state-error-fg shadow"
                        >
                          ♥
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {!tradeInMode && (
                  <div className="w-full max-w-[8rem] text-center">
                    <p
                      className="truncate font-body text-sm font-semibold"
                      title={share.snapshot_display_name}
                    >
                      {share.snapshot_display_name}
                    </p>
                    <p
                      className="truncate font-body text-[11px] opacity-60"
                      title={share.portfolio_title}
                    >
                      {share.portfolio_title}
                    </p>
                  </div>
                )}
                {tradeInMode && hasActiveListing && (
                  <span
                    className="font-body text-[11px] text-tier-founders-fg"
                    title="This certificate is listed for sale — delist it first to trade it in."
                  >
                    Delist first
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pageInfo && (items.length > 0 || page > 1) && (
        <PaginationFooter
          page={page}
          perPage={perPage}
          totalPages={Math.max(
            1,
            Math.ceil(pageInfo.total_items / pageInfo.page_size),
          )}
          hasNext={pageInfo.has_next}
          onPageChange={(p) =>
            updateParam("page", p === 1 ? null : String(p))
          }
          onPerPageChange={(pp) =>
            updateParam("per_page", pp === DEFAULT_PAGE_SIZE ? null : String(pp))
          }
        />
      )}
      </section>

      {/* Collection item detail popup — grows out from the clicked card;
          hosts the per-item actions (open / list / view details). */}
      {detailTarget && (
        <CollectionItemDialog
          item={detailTarget}
          origin={detailOrigin}
          onClose={() => setDetailTarget(null)}
          onOpenDossier={handleOpenSealed}
          onListSealed={setListForSaleTarget}
          onListCertificate={setListCertificateTarget}
          onEditListing={setEditListingItem}
        />
      )}

      {/* Edit-listing dialog for an already-listed collection item (opened
          from the detail popup's "Edit listing" affordance). */}
      {editListingItem?.kind === "opened" && editListingItem.active_listing && (
        <EditShareListingDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditListingItem(null);
          }}
          listingId={editListingItem.active_listing.listing_id}
          creatorUserId={editListingItem.share.creator_id}
          tier={editListingItem.share.tier as ShareTier}
          signed={editListingItem.share.signed}
          priceCents={editListingItem.active_listing.price_cents}
          allowBidding={editListingItem.active_listing.allow_bidding}
        />
      )}
      {editListingItem?.kind === "sealed" && editListingItem.active_listing && (
        <EditSealedDossierListingDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditListingItem(null);
          }}
          listingId={editListingItem.active_listing.listing_id}
          dropId={editListingItem.sealed_dossier.drop_id}
          priceCents={editListingItem.active_listing.price_cents}
          allowBidding={editListingItem.active_listing.allow_bidding}
        />
      )}

      {/* Epic 10 — right-click showcase menu (add/remove), positioned at the
          cursor. Closes on outside click / Escape / scroll. */}
      {menu && (
        <ShowcaseContextMenu
          target={menu}
          onToggle={toggleShowcaseMembership}
          onClose={() => setMenu(null)}
        />
      )}

      {openingSealed && (
        <RevealCeremonyMount
          row={openingSealed}
          onClose={handleCeremonyComplete}
          onViewCertificate={(share) =>
            navigate(`/vault/certificates/${share.share_id}`)
          }
        />
      )}

      {/* Story 5.3 — "List for sale" dialog (sealed dossiers), mounted at
          the page level so its lifecycle is independent of the grid
          render. The detail popup sets listForSaleTarget. */}
      <ListSealedDossierForSaleDialog
        open={listForSaleTarget !== null}
        onOpenChange={(open) => {
          if (!open) setListForSaleTarget(null);
        }}
        sealedDossierId={listForSaleTarget?.id ?? ""}
        dropId={listForSaleTarget?.drop_id ?? ""}
      />

      {/* "List for sale" dialog for opened certificates — the detail popup
          sets listCertificateTarget. */}
      <ListForSaleDialog
        open={listCertificateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setListCertificateTarget(null);
        }}
        shareId={listCertificateTarget?.share.share_id ?? ""}
        creatorUserId={listCertificateTarget?.share.creator_id ?? ""}
        tier={(listCertificateTarget?.share.tier ?? "common") as ShareTier}
        signed={listCertificateTarget?.share.signed ?? false}
      />
    </main>
    </div>
  );
}

interface FilterBarProps {
  filter: VaultFilter;
  tier: VaultTier;
  sort: VaultSort;
  onFilterChange: (v: VaultFilter) => void;
  onTierChange: (v: VaultTier) => void;
  onSortChange: (v: string) => void;
}

function FilterBar({
  filter,
  tier,
  sort,
  onFilterChange,
  onTierChange,
  onSortChange,
}: FilterBarProps) {
  const selectClass =
    "rounded-md border border-brand-forest-700 bg-brand-forest-800 px-3 py-2 text-sm font-body text-brand-paper-100 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-brand-forest-500";
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs font-body opacity-80">
        State
        <select
          className={selectClass}
          value={filter}
          onChange={(e) => onFilterChange(e.target.value as VaultFilter)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-body opacity-80">
        Tier
        <select
          className={selectClass}
          value={tier}
          onChange={(e) => onTierChange(e.target.value as VaultTier)}
        >
          {TIER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-body opacity-80">
        Sort
        <select
          className={selectClass}
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

interface PaginationFooterProps {
  page: number;
  perPage: PageSize;
  totalPages: number;
  hasNext: boolean;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: PageSize) => void;
}

function PaginationFooter({
  page,
  perPage,
  totalPages,
  hasNext,
  onPageChange,
  onPerPageChange,
}: PaginationFooterProps) {
  return (
    <nav
      aria-label="Collection pagination"
      className="flex flex-wrap items-center justify-between gap-3 pt-2"
    >
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          ← Previous
        </Button>
        <span className="font-body text-sm opacity-80" aria-live="polite">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="secondary"
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
        >
          Next →
        </Button>
      </div>
      <label className="flex items-center gap-2 text-xs font-body opacity-80">
        Items per page
        <select
          className="rounded-md border border-brand-forest-700 bg-brand-forest-800 px-2 py-1 text-sm text-brand-paper-100 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-brand-forest-500"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value) as PageSize)}
        >
          {PAGE_SIZE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </label>
    </nav>
  );
}

/// "Listed" marker — overlays the top-left corner of a collection artifact
/// whose item currently has an ACTIVE marketplace listing, so the owner can
/// see at a glance what is up for sale. Mirrors the marketplace card's
/// "Your listing" pill treatment (gold = marketplace signal).
function ListedBadge() {
  return (
    <span
      data-testid="vault-listed-badge"
      className="pointer-events-none absolute left-1 top-1 z-10 rounded-full bg-brand-gold-500 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-ink-900 shadow"
    >
      Listed
    </span>
  );
}

/// "In showcase" marker — overlays an artifact the owner has published to
/// their public collection (Epic 10). Gold ✦ to echo the right-click
/// "Showcase" action; sits top-right so it doesn't collide with the top-left
/// "Listed" pill or the bottom-right highlight/favourite cluster.
function ShowcaseBadge() {
  return (
    <span
      data-testid="vault-showcase-badge"
      aria-label="In your public showcase"
      className="pointer-events-none absolute right-1 top-1 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-gold-500 text-[10px] text-brand-ink-900 shadow"
    >
      ✦
    </span>
  );
}

function VaultGridSkeleton() {
  // 6 skeleton placeholders matching the responsive col count. The
  // grid container's aria-busy=true (set on the real <ul>) carries the
  // loading signal; the skeletons are decorative.
  return (
    <ul
      role="list"
      aria-busy="true"
      aria-label="Loading collection items"
      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
    >
      {Array.from({ length: 6 }).map((_, idx) => (
        <li key={idx} role="listitem" className="flex justify-center">
          <Skeleton className="w-24 h-32 rounded-md" />
        </li>
      ))}
    </ul>
  );
}

function EmptyState() {
  return (
    <SharedEmptyState
      aria-label="Empty collection"
      title="Your collection is waiting"
      description="Every sealed dossier and opened certificate you own will appear here."
      action={
        <Link to="/marketplace">
          <Button variant="ghost">Browse the marketplace →</Button>
        </Link>
      }
    />
  );
}

/// Inner mount owning the reveal mutation. The ceremony opens in its
/// `approach` state (sealed dossier); "Break the seal" fires the real
/// reveal (onBreak), and the returned share rises out of the opening
/// dossier. The idempotency key is per-mount so a "Watch again" replay
/// re-requests the SAME committed outcome (cached) rather than re-rolling.
function RevealCeremonyMount({
  row,
  onClose,
  onViewCertificate,
}: {
  row: SealedDossierVaultRow;
  onClose: () => void;
  onViewCertificate: (share: CertificateShare) => void;
}) {
  const reducedMotion = useEffectiveMotionMode() === "reduced-motion";
  const reveal = useReveal();
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  const share = reveal.data ?? null;

  // The dossier's creator roster + theme for the approach screen (which
  // creator is inside stays secret — this is the whole possible roster, as
  // shown on the shop hero). Cheap cached fetch by drop_id.
  const drop = useDropForPurchase(row.drop_id);
  const creators = useMemo(
    () =>
      (drop.data?.creators ?? []).map((c) => ({
        name: c.display_name || (c.handle ? `@${c.handle}` : ""),
        img: c.image_signed_url,
      })),
    [drop.data],
  );

  return (
    <RevealCeremony
      open
      dossier={dossierFromVaultRow(row)}
      share={share}
      certificate={
        share ? (
          <PatronageCertificate
            {...certPropsFromShare(share, { withQuote: true })}
          />
        ) : null
      }
      serial={share ? certSerialFromShareId(share.share_id) : undefined}
      theme={drop.data?.portfolio_theme ?? null}
      creators={creators}
      reducedMotion={reducedMotion}
      onBreak={() =>
        reveal.mutate(
          { sealedDossierId: row.id, idempotencyKey },
          {
            onError: (error) => {
              toast.error(error.message);
              onClose();
            },
          },
        )
      }
      onViewCertificate={onViewCertificate}
      onClose={onClose}
    />
  );
}
